<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { breakpointsTailwind } from '@vueuse/core'
import Popper from 'vue3-popper'
import type { Elemental, ElementalType } from '~~/src/types/elemental'
interface IProps {
  element: Elemental
  elementalType: ElementalType
  variant?: 'default' | 'mini' | 'default-elemental' | 'clear'
  disablePopup?: boolean
}

interface IEmits {
  (event: 'updateSaved', e: number): void
  (event: 'popperToggled', show: boolean): void
}

const props = withDefaults(defineProps<IProps>(), { variant: 'default' })

const emit = defineEmits<IEmits>()

defineOptions({
  name: 'SaveElemental',
})

const { element, variant, elementalType, disablePopup } = toRefs(props)

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

const user = useUser()
const { colors } = useTheme()

const isSaved = ref(false)
const isSaving = ref(false)

const popperRef = ref()
const showPopper = ref(false)

const canShowPopup = computed(() => {
  return !disablePopup && isSaved.value && elementalType.value?.name !== 'list'
})

watch(element, () => {
  isSaved.value = element.value?.isSaved || false
})

watch(user, () => {
  if (!user.value && element.value)
    isSaved.value = false
})

const saveUpdate = useUpdateElementalSave()
watch(saveUpdate, (update) => {
  if (update?.id === element.value.id) {
    element.value.isSaved = update?.state === 'added'
    isSaved.value = update?.state === 'added'
  }
})

const queryClient = useQueryClient()
async function handleSave() {
  if (isSaving.value)
    return

  isSaving.value = true

  const updater = {
    ...element.value,
    isSaved: isSaved.value,
  }
  await handleElementalSave(updater)
  queryClient.refetchQueries({
    queryKey: ['profile-list-elementals', elementalType.value.id],
    exact: false,
    type: 'all',
  })

  isSaving.value = false

  emit('updateSaved', element.value?.nSaves || 0)
  if (canShowPopup.value)
    showPopper.value = true
}

async function onPopperOpen() {
  emit('popperToggled', true)
  showPopper.value = true
}

function onPopperClose() {
  emit('popperToggled', false)
  showPopper.value = false
}
</script>

<script lang="ts">
export default defineComponent({
  name: 'ElementalSave',
})
</script>

<template>
  <template v-if="canShowPopup">
    <ClientOnly v-if="!isMobile">
      <Popper
        ref="popperRef"
        hover placement="bottom-end" offset-distance="4"
        class="<md:hidden"
        @open:popper="onPopperOpen" @close:popper="onPopperClose"
      >
        <AppButtonIcon
          v-if="variant === 'default-elemental' || variant === 'clear'"
          :size="variant === 'clear' ? '28px' : '40px'"
          :font-size="variant === 'clear' ? '20px' : undefined"
          @click.prevent.stop="() => showPopper = !showPopper"
        >
          <div v-if="isSaved" i-ri:bookmark-fill />
          <div v-else i-ri:bookmark-line />
        </AppButtonIcon>

        <button
          v-else-if="element"
          flex items-center center
          bg-white border border-soft-gray
          rounded-full cursor-pointer duration-125
          color-bluish-softer hover:text-primary hover:border-primary transition-colors
          text="12px"
          :h="variant === 'mini' ? 8 : 10"
          :pl="variant === 'mini' ? '7px' : '3'"
          :pr="variant === 'mini' ? '5px' : '3'"
          @click.prevent.stop="() => showPopper = !showPopper"
        >
          <div v-if="isSaved" i-ri:bookmark-fill />
          <div v-else i-ri:bookmark-line />
          <div :ml="variant === 'default' ? '1' : '0'" i-mdi:chevron-down text="16px" />
        </button>

        <template #content>
          <div class="-my-1 py-1">
            <AppFloatingMenu
              col cursor-default w-70 border-soft-gray
              :style="{ maxHeight: '19rem' }"
              @click.prevent.stop
            >
              <ElementalSavePopup :element="element" :elemental-type="elementalType" :popup-shown="showPopper" @update-saved="emit('updateSaved', $event)" />
            </AppFloatingMenu>
          </div>
        </template>
      </Popper>
    </ClientOnly>
    <template v-else>
      <AppButtonIcon
        v-if="variant === 'default-elemental' || variant === 'clear'"
        :size="variant === 'clear' ? '28px' : '40px'"
        :font-size="variant === 'clear' ? '20px' : undefined"
        @click.prevent.stop="() => showPopper = !showPopper"
      >
        <div v-if="isSaved" i-ri:bookmark-fill />
        <div v-else i-ri:bookmark-line />
      </AppButtonIcon>

      <button
        v-else-if="element"
        flex items-center center
        bg-white border border-soft-gray
        rounded-full cursor-pointer duration-125
        color-bluish-softer hover:text-primary hover:border-primary transition-colors
        text="12px"
        :h="variant === 'mini' ? 8 : 10"
        :pl="variant === 'mini' ? '7px' : '3'"
        :pr="variant === 'mini' ? '5px' : '3'"
        @click.prevent.stop="() => showPopper = !showPopper"
      >
        <div v-if="isSaved" i-ri:bookmark-fill />
        <div v-else i-ri:bookmark-line />
        <div :ml="variant === 'default' ? '1' : '0'" i-mdi:chevron-down text="16px" />
      </button>

      <Teleport to="body">
        <TransitionSlide direction="down">
          <div v-if="showPopper && isMobile" class="fixed top-0 left-0 right-0 bottom-0 z-1000">
            <div class="absolute bottom-0 left-0 right-0 top-0 z-999  bg-black opacity-20" @click="showPopper = false" />

            <AppFloatingDrawer>
              <div class="col max-h-[75vh] h-max rounded-t-5 bg-white w-full relative overflow-auto">
                <header class="z-999 sticky top-0 flex p-5 pb-0 justify-end items-center h-10 rounded-t-5 bg-white">
                  <AppButton variant="text" :text-style="{ color: colors.purple }" mr="-10px" @click="showPopper = false">
                    Done
                  </AppButton>
                </header>

                <div col flex-1 overflow-auto>
                  <ElementalSavePopup :element="element" :elemental-type="elementalType" :popup-shown="showPopper" @update-saved="emit('updateSaved', $event)" />
                </div>
              </div>
            </AppFloatingDrawer>
          </div>
        </TransitionSlide>
      </Teleport>
    </template>
  </template>
  <template v-else>
    <AppButtonIcon
      v-if="variant === 'default-elemental' || variant === 'clear'"
      :size="variant === 'clear' ? '28px' : undefined"
      :font-size="variant === 'clear' ? '20px' : undefined"
      @click.prevent.stop="handleSave()"
    >
      <div v-if="isSaved" i-ri:bookmark-fill />
      <div v-else i-ri:bookmark-line />
    </AppButtonIcon>
    <button
      v-else
      flex items-center center
      bg-white border border-soft-gray
      rounded-full cursor-pointer duration-125
      color-bluish-softer hover:text-primary hover:border-primary transition-colors
      text="12px"
      :h="variant === 'mini' ? 8 : 10"
      :w="variant === 'mini' ? 8 : 10"
      @click.prevent.stop="handleSave()"
    >
      <div v-if="isSaved" i-ri:bookmark-fill />
      <div v-else i-ri:bookmark-line />
    </button>
  </template>
</template>
